import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Ship, Anchor, FileCheck2, TrendingUp } from "lucide-react";
import { Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { fmtMoney, fmtNum, fmtDate, pnlColor } from "../lib/format";
import StatusPill from "../components/StatusPill";
import DataTable from "../components/DataTable";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import EntityFormDialog from "../components/EntityFormDialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../components/ui/sheet";

const STAGES = ["Nominated", "Laycan Agreed", "Loading", "In Transit", "Discharge", "Settled"];
const STAGE_COLOR = {
  Nominated: "amber", "Laycan Agreed": "sky", Loading: "blue", "In Transit": "purple", Discharge: "orange", Settled: "emerald",
};

function PnlRow({ label, value, bold }) {
  return (
    <div className={`flex justify-between py-1.5 text-sm ${bold ? "font-bold border-t border-slate-200 dark:border-slate-700 pt-2" : ""}`}>
      <span className="text-slate-600 dark:text-slate-300">{label}</span>
      <span className={`font-mono ${pnlColor(value)}`}>{fmtMoney(value)}</span>
    </div>
  );
}

function VesselDetail({ vessel, onClose }) {
  const { data: pnl } = useQuery({ queryKey: ["vpnl", vessel?.id], queryFn: () => api.get(`/vessels/${vessel.id}/pnl`), enabled: !!vessel });
  const { data: tasks = [] } = useQuery({ queryKey: ["tasks"], queryFn: () => api.get("/tasks"), enabled: !!vessel });
  const { data: docs = [] } = useQuery({ queryKey: ["documents"], queryFn: () => api.get("/documents"), enabled: !!vessel });
  if (!vessel) return null;
  const vtasks = tasks.filter((t) => t.vessel_id === vessel.id).sort((a, b) => a.order - b.order);
  const vdocs = docs.filter((d) => d.vessel_id === vessel.id);

  return (
    <Sheet open={!!vessel} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto erp-scroll" data-testid="vessel-detail">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Ship className="h-5 w-5 text-orange-600" />{vessel.name}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-5">
          <div className="flex flex-wrap gap-2">
            <StatusPill status={vessel.status} />
            <span className="text-xs bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">DWT {fmtNum(vessel.dwt)}</span>
            <span className="text-xs bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">{vessel.load_port} → {vessel.discharge_port}</span>
          </div>

          {pnl && (
            <div className="bg-slate-50 dark:bg-slate-800/40 rounded-lg p-4 border border-slate-200 dark:border-slate-800">
              <h4 className="font-bold text-sm mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Live Vessel P&L</h4>
              <PnlRow label={`Sale Value (${fmtNum(pnl.sale_qty)} MT @ ${fmtMoney(pnl.avg_sale_price)})`} value={pnl.sale_value} />
              <PnlRow label={`Purchase Cost (${fmtNum(pnl.purchase_qty)} MT @ ${fmtMoney(pnl.avg_purchase_price)})`} value={-pnl.purchase_cost} />
              <PnlRow label="Freight" value={-pnl.freight_cost} />
              <PnlRow label="Demurrage / Despatch" value={-pnl.demurrage} />
              <PnlRow label="Other Costs (ins/port/survey/brokerage)" value={-pnl.other_costs} />
              <PnlRow label="Financing Cost" value={-pnl.financing_cost} />
              <PnlRow label="Physical P&L" value={pnl.physical_pnl} bold />
              <PnlRow label="Hedge Realised" value={pnl.hedge_realised} />
              <PnlRow label="Hedge Unrealised (MTM)" value={pnl.hedge_unrealised} />
              <PnlRow label="Net All-in P&L" value={pnl.net_pnl} bold />
              <p className="text-xs text-slate-400 mt-2">Back-to-back coverage: <span className="font-semibold">{pnl.coverage_pct}%</span></p>
            </div>
          )}

          <div>
            <h4 className="font-bold text-sm mb-2 flex items-center gap-2"><Anchor className="h-4 w-4" /> Task Pipeline</h4>
            <div className="space-y-1.5">
              {vtasks.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-sm py-1.5 px-2 rounded bg-slate-50 dark:bg-slate-800/40">
                  <span className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${t.status === "Done" ? "bg-emerald-500" : t.status === "In Progress" ? "bg-blue-500" : "bg-slate-300"}`} />
                    {t.title}
                  </span>
                  <span className="flex items-center gap-2"><span className="text-xs text-slate-400">{t.assignee}</span><StatusPill status={t.status} /></span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-bold text-sm mb-2 flex items-center gap-2"><FileCheck2 className="h-4 w-4" /> Documents</h4>
            <div className="grid grid-cols-2 gap-2">
              {vdocs.map((d) => (
                <div key={d.id} className="flex items-center justify-between text-xs py-1.5 px-2 rounded border border-slate-200 dark:border-slate-800">
                  <span>{d.type}</span><StatusPill status={d.status} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Vessels() {
  const { data: vessels = [] } = useQuery({ queryKey: ["pipeline"], queryFn: () => api.get("/vessels-pipeline"), refetchInterval: 20000 });
  const [sel, setSel] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const vesselFields = [
    { key: "name", label: "Vessel Name", type: "text", required: true },
    { key: "status", label: "Stage", type: "select", options: STAGES, default: "Nominated", required: true },
    { key: "dwt", label: "DWT", type: "number", default: 40000 },
    { key: "flag", label: "Flag", type: "text", default: "Singapore" },
    { key: "owner", label: "Owner / Operator", type: "text", default: "Ocean Carriers Ltd" },
    { key: "load_port", label: "Load Port", type: "text", required: true },
    { key: "discharge_port", label: "Discharge Port", type: "text", required: true },
    { key: "laycan_start", label: "Laycan Start", type: "date" },
    { key: "laycan_end", label: "Laycan End", type: "date" },
    { key: "financing_cost", label: "Financing Cost", type: "number", default: 0 },
    { key: "insurance_cost", label: "Insurance Cost", type: "number", default: 0 },
    { key: "port_charges", label: "Port Charges", type: "number", default: 0 },
    { key: "survey_fees", label: "Survey Fees", type: "number", default: 0 },
    { key: "brokerage", label: "Brokerage", type: "number", default: 0 },
  ];

  const cols = [
    { key: "name", label: "Vessel", render: (r) => <span className="font-semibold">{r.name}</span> },
    { key: "status", label: "Stage", render: (r) => <StatusPill status={r.status} /> },
    { key: "load_port", label: "Route", render: (r) => <span className="text-xs">{r.load_port} → {r.discharge_port}</span> },
    { key: "laycan_start", label: "Laycan", render: (r) => <span className="text-xs">{fmtDate(r.laycan_start)}</span> },
    { key: "coverage_pct", label: "Coverage", align: "right", render: (r) => `${r.coverage_pct}%` },
    { key: "net_pnl", label: "Net P&L", align: "right", render: (r) => <span className={pnlColor(r.net_pnl) + " font-semibold"}>{fmtMoney(r.net_pnl)}</span> },
  ];

  return (
    <div data-testid="vessels-page">
      <Tabs defaultValue="pipeline">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="pipeline" data-testid="tab-pipeline">Pipeline (Kanban)</TabsTrigger>
            <TabsTrigger value="list" data-testid="tab-vessel-list">List View</TabsTrigger>
          </TabsList>
          <Button data-testid="vessel-add" size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={() => setFormOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> New Vessel
          </Button>
        </div>
        <TabsContent value="pipeline">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {STAGES.map((stage) => {
              const items = vessels.filter((v) => v.status === stage);
              return (
                <div key={stage} className="bg-slate-100/60 dark:bg-slate-900/60 rounded-lg p-2 min-h-[200px]" data-testid={`kanban-col-${stage}`}>
                  <div className="flex items-center justify-between px-1 mb-2">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">{stage}</p>
                    <span className="text-xs bg-white dark:bg-slate-800 rounded-full px-1.5">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {items.map((v) => (
                      <button key={v.id} data-testid={`vessel-card-${v.id}`} onClick={() => setSel(v)}
                        className="w-full text-left bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 border-l-4 border-l-orange-500 rounded-md p-2.5 shadow-sm hover:shadow-md transition-all">
                        <p className="font-semibold text-sm truncate">{v.name}</p>
                        <p className="text-xs text-slate-400 truncate">{v.load_port} → {v.discharge_port}</p>
                        <p className={`text-xs font-mono font-semibold mt-1 ${pnlColor(v.net_pnl)}`}>{fmtMoney(v.net_pnl)}</p>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
        <TabsContent value="list">
          <DataTable testid="vessel-list-table" title="Vessel Register" columns={cols} rows={vessels} csvName="vessels" onRowClick={setSel} />
        </TabsContent>
      </Tabs>
      <VesselDetail vessel={sel} onClose={() => setSel(null)} />
      <EntityFormDialog open={formOpen} onClose={() => setFormOpen(false)} title="New Vessel / Shipment"
        path="vessels" fields={vesselFields} testid="vessel-form" />
    </div>
  );
}
